import { Alert, alpha, Box, CircularProgress, Grid, Paper, Typography, useTheme } from "@mui/material";
import { BarChart } from '@mui/x-charts/BarChart';
import { useMemo } from "react";
import { useCommitmentsGraph } from "../hooks/useCommitmentsGraph";

const MILLIONS_DIVISOR = 1_000_000;
const CHART_HEIGHT = 300;
const CATEGORY_GAP_RATIO = 0.3;
const BAR_GAP_RATIO = 0.1;
const COLOR_ALPHA = 0.8;

interface ChartDataItem {
    label: string;
    value: number;
}

interface CommitmentChartProps {
    title: string;
    data: ChartDataItem[];
    tickMinStep: number;
    color: string;
}

const aggregateDataByKey = <T extends Record<string, any>>(
    data: T[],
    keyExtractor: (item: T) => string,
    valueKey: keyof T
): Record<string, number> => {
    return data.reduce((acc, item) => {
        const key = keyExtractor(item);
        const value = parseFloat(item[valueKey]) / MILLIONS_DIVISOR;
        acc[key] = (acc[key] || 0) + value;
        return acc;
    }, {} as Record<string, number>);
};

const transformToChartData = (aggregatedData: Record<string, number>): ChartDataItem[] => {
    return Object.entries(aggregatedData)
        .map(([label, value]) => ({ label, value }))
        .sort((a, b) => b.value - a.value);
};

const CommitmentChart = ({ title, data, tickMinStep, color }: CommitmentChartProps) => (
    <Paper sx={{ p: 2 }} variant="outlined">
        <Typography variant="subtitle2" color="text.primary" sx={{ mb: 2 }}>
            {title}
        </Typography>
        <BarChart
            yAxis={[{
                scaleType: 'band',
                data: data.map(d => d.label),
                categoryGapRatio: CATEGORY_GAP_RATIO,
                barGapRatio: BAR_GAP_RATIO,
            }]}
            series={[{
                data: data.map(d => d.value),
                color,
            }]}
            layout="horizontal"
            height={CHART_HEIGHT}
            xAxis={[{
                label: '£M',
                tickMinStep,
            }]}
        />
    </Paper>
);

export const CommitmentsGraph = () => {

    const theme = useTheme();
    const { data, isLoading, isError, error } = useCommitmentsGraph();

    const { supplierChartData, phaseChartData } = useMemo(() => {
        if (isLoading || isError) {
            return {
                supplierChartData: [],
                phaseChartData: [],
            };
        }
        const supplierData = aggregateDataByKey(data?.breakdown_by_supplier, item => item.supplier_name, 'committed_value');
        const phaseData = aggregateDataByKey(data?.breakdown_by_phase, item => item.phase_name, 'committed_value');
        return {
            supplierChartData: transformToChartData(supplierData),
            phaseChartData: transformToChartData(phaseData),
        };
    }, [data]);

    const chartColor = alpha(theme.palette.primary.main, COLOR_ALPHA);

    if (isLoading) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
                my={5}
            >
                <CircularProgress />
            </Box>
        );
    };

    if (isError) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
            >
                <Alert
                    severity="warning"
                    sx={{
                        width: "auto",
                        justifyContent: "center",
                        textAlign: "center",
                    }}
                >
                    {error?.message || "Something went wrong!"}
                </Alert>
            </Box>
        );
    };

    return (
        <Grid container spacing={2} sx={{ mb: 3 }} px={2}>
            <Grid size={{ xs: 12, md: 6 }}>
                <CommitmentChart
                    title="Commitments by Supplier"
                    data={supplierChartData}
                    tickMinStep={2.5}
                    color={chartColor}
                />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
                <CommitmentChart
                    title="Commitments by Phase"
                    data={phaseChartData}
                    tickMinStep={5.5}
                    color={chartColor}
                />
            </Grid>
        </Grid>
    );
};